import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Activity,
  LogIn,
  LogOut,
  Globe,
  MousePointerClick,
  Monitor,
  MapPin,
  Clock,
  ChevronDown,
  ChevronUp,
  Loader2,
  Calendar,
  Smartphone,
} from "lucide-react";
import { format, formatDistanceToNow, isToday, isYesterday } from "date-fns";
import RightDrawer from "../../../shared/components/form/RightDrawer";
import { userActivityService } from "../../../services/userActivityService";
import type { UserActivity } from "../../../types/userActivity";

type UserActivityType = UserActivity["type"];
import { BRAND_COLORS } from "../../../shared/constants/brandColors";

interface UserActivityDrawerProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
}

type DayGroup = { dateLabel: string; dateKey: string; activities: UserActivity[] };

function getActivityIcon(type: UserActivityType) {
  switch (type) {
    case "login":
      return <LogIn size={18} className="text-green-600" />;
    case "logout":
      return <LogOut size={18} className="text-amber-600" />;
    case "page_view":
      return <Globe size={18} className="text-blue-600" />;
    case "action":
      return <MousePointerClick size={18} className="text-violet-600" />;
    case "session_start":
      return <Monitor size={18} className="text-cyan-600" />;
    default:
      return <Activity size={18} className="text-gray-600" />;
  }
}

function getActivityLabel(type: UserActivityType, meta?: UserActivity["metadata"]) {
  const desc = meta?.description;
  if (desc) return desc;
  switch (type) {
    case "login":
      return "Logged in";
    case "logout":
      return "Logged out";
    case "page_view":
      return meta?.path ? `Viewed ${meta.path}` : "Viewed page";
    case "action":
      return meta?.description || "Performed action";
    case "session_start":
      return "Session started";
    default:
      return type;
  }
}

function getDeviceIcon(deviceType?: string) {
  if (deviceType === "mobile") return <Smartphone size={14} />;
  return <Monitor size={14} />;
}

function groupByDay(activities: UserActivity[]): DayGroup[] {
  const map = new Map<string, UserActivity[]>();
  for (const a of activities) {
    const d = new Date(a.timestamp);
    const key = format(d, "yyyy-MM-dd");
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(a);
  }
  const sortedKeys = Array.from(map.keys()).sort((a, b) => b.localeCompare(a));
  return sortedKeys.map((dateKey) => {
    const d = new Date(dateKey);
    let dateLabel: string;
    if (isToday(d)) dateLabel = "Today";
    else if (isYesterday(d)) dateLabel = "Yesterday";
    else dateLabel = format(d, "EEEE, MMM d, yyyy");
    return {
      dateKey,
      dateLabel,
      activities: (map.get(dateKey) ?? []).sort(
        (x, y) => new Date(y.timestamp).getTime() - new Date(x.timestamp).getTime()
      ),
    };
  });
}

export default function UserActivityDrawer({
  open,
  onClose,
  userId,
  userName,
}: UserActivityDrawerProps) {
  const [loading, setLoading] = useState(false);
  const [activities, setActivities] = useState<UserActivity[]>([]);
  const [pagination, setPagination] = useState<{
    page: number;
    limit: number;
    totalDocs: number;
    totalPages: number;
  } | null>(null);
  const [page, setPage] = useState(1);
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open || !userId) return;
    setLoading(true);
    setPage(1);
    userActivityService
      .getUserActivity(userId, { limit: 100, page: 1 })
      .then((res) => {
        setActivities(res.data);
        setPagination(res.pagination);
      })
      .catch(() => {
        setActivities([]);
        setPagination(null);
      })
      .finally(() => setLoading(false));
  }, [open, userId]);

  const loadMore = () => {
    if (!pagination || page >= pagination.totalPages) return;
    setLoading(true);
    userActivityService
      .getUserActivity(userId, { limit: 100, page: page + 1 })
      .then((res) => {
        setActivities((prev) => [...prev, ...res.data]);
        setPagination(res.pagination);
        setPage(res.pagination.page);
      })
      .finally(() => setLoading(false));
  };

  const dayGroups = groupByDay(activities);
  const toggleDay = (dateKey: string) => {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(dateKey)) next.delete(dateKey);
      else next.add(dateKey);
      return next;
    });
  };

  return (
    <RightDrawer
      open={open}
      onClose={onClose}
      title=""
      size="xl"
    >
      <div className="flex flex-col h-full">
        {/* Header */}
        <div
          className="flex-shrink-0 px-6 py-4 border-b border-gray-200"
          style={{
            background: `linear-gradient(135deg, ${BRAND_COLORS.primaryLight} 0%, ${BRAND_COLORS.primarySoft} 100%)`,
          }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center border"
              style={{
                backgroundColor: BRAND_COLORS.primary + "18",
                borderColor: BRAND_COLORS.primaryBorder,
              }}
            >
              <Activity size={24} style={{ color: BRAND_COLORS.primary }} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">User Activity</h2>
              <p className="text-sm text-gray-600">{userName}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && activities.length === 0 ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={32} className="animate-spin text-gray-400" />
            </div>
          ) : dayGroups.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <Activity size={48} className="mx-auto mb-4 opacity-50" />
              <p>No activity recorded yet.</p>
              <p className="text-sm mt-1">Logins and actions will appear here.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {dayGroups.map((group) => {
                const isExpanded = expandedDays.has(group.dateKey) || expandedDays.size === 0;
                return (
                  <motion.div
                    key={group.dateKey}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm"
                  >
                    <button
                      type="button"
                      onClick={() => toggleDay(group.dateKey)}
                      className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <Calendar size={18} className="text-gray-500" />
                        <span className="font-medium text-gray-900">{group.dateLabel}</span>
                        <span className="text-sm text-gray-500">
                          ({group.activities.length} {group.activities.length === 1 ? "event" : "events"})
                        </span>
                      </div>
                      {isExpanded ? (
                        <ChevronUp size={18} className="text-gray-500" />
                      ) : (
                        <ChevronDown size={18} className="text-gray-500" />
                      )}
                    </button>
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="border-t border-gray-100"
                        >
                          <div className="divide-y divide-gray-50">
                            {group.activities.map((activity, idx) => (
                              <div
                                key={activity._id || `${activity.timestamp}-${idx}`}
                                className="flex gap-4 px-4 py-3 hover:bg-gray-50/50 transition-colors"
                              >
                                <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center">
                                  {getActivityIcon(activity.type)}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900">
                                    {getActivityLabel(activity.type, activity.metadata)}
                                  </p>
                                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-xs text-gray-500">
                                    <span className="flex items-center gap-1">
                                      <Clock size={12} />
                                      {format(new Date(activity.timestamp), "h:mm a")}
                                      <span className="text-gray-400">
                                        ({formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })})
                                      </span>
                                    </span>
                                    {activity.ip && (
                                      <span className="flex items-center gap-1" title="IP">
                                        {activity.ip}
                                      </span>
                                    )}
                                    {(activity.city || activity.country) && (
                                      <span className="flex items-center gap-1">
                                        <MapPin size={12} />
                                        {[activity.city, activity.country].filter(Boolean).join(", ")}
                                      </span>
                                    )}
                                    {(activity.deviceType || activity.browser || activity.os) && (
                                      <span className="flex items-center gap-1">
                                        {getDeviceIcon(activity.deviceType)}
                                        {[activity.deviceType, activity.browser, activity.os]
                                          .filter(Boolean)
                                          .join(" · ")}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>
          )}

          {pagination && page < pagination.totalPages && (
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                onClick={loadMore}
                disabled={loading}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {loading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <>
                    Load more · Page {page} of {pagination.totalPages}
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </RightDrawer>
  );
}
